import React, { useState, useEffect } from 'react'
import { Form, Col, Table, Badge, Card } from "react-bootstrap"
import Title from "../../../utilities/other/title"
import "./table.css"

const SUPPORTIVE_CRITERIA = [
    {
        id: 'pd_sc_1',
        label: 'Clear and dramatic beneficial response to dopaminergic therapy. During initial treatment, patient returned to normal or near-normal level of function. In the absence of clear documentation of initial response a dramatic response can be classified as: a) Marked improvement with dose increases or marked worsening with dose decreases (>30% in UPDRS III with change in treatment, or clearly-documented history of marked changes from a reliable patient or caregiver). b) Unequivocal and marked on/off fluctuations, which must have at some point included predictable end-of-dose wearing off.'
    },
    {
        id: 'pd_sc_2',
        label: 'Presence of levodopa-induced dyskinesia'
    },
    {
        id: 'pd_sc_3',
        label: 'Rest tremor of a limb, documented on clinical examination (in past, or on current examination)'
    },
    {
        id: 'pd_sc_4',
        label: 'The presence of either olfactory loss or cardiac sympathetic denervation on MIBG scintigraphy'
    }
]

const ABSOLUTE_EXCLUSION_CRITERIA = [
    {
        id: 'pd_ae_1',
        label: 'Unequivocal cerebellar abnormalities, such as cerebellar gait, limb ataxia, or cerebellar oculomotor abnormalities (eg, sustained gaze evoked nystagmus, macro square wave jerks, hypermetric saccades)'
    },
    {
        id: 'pd_ae_2',
        label: 'Downward vertical supranuclear gaze palsy, or selective slowing of downward vertical saccades'
    },
    {
        id: 'pd_ae_3',
        label: 'Diagnosis of probable behavioral variant frontotemporal dementia or primary progressive aphasia, defined according to consensus criteria within the first 5 years of disease'
    },
    {
        id: 'pd_ae_4',
        label: 'Parkinsonian features restricted to the lower limbs for more than 3 years'
    },
    {
        id: 'pd_ae_5',
        label: 'Treatment with a dopamine receptor blocker or a dopamine-depleting agent in a dose and time-course consistent with drug-induced parkinsonism'
    },
    {
        id: 'pd_ae_6',
        label: 'Absence of observable response to high-dose levodopa despite at least moderate severity of disease'
    },
    {
        id: 'pd_ae_7',
        label: 'Unequivocal cortical sensory loss (ie, graphesthesia, stereognosis with intact primary sensory modalities), clear limb ideomotor apraxia, or progressive aphasia'
    },
    {
        id: 'pd_ae_8',
        label: 'Normal functional neuroimaging of the presynaptic dopaminergic system'
    },
    {
        id: 'pd_ae_9',
        label: "Documentation of an alternative condition known to produce parkinsonism and plausibly connected to the patient's symptoms, or, the expert evaluating physician, based on the full diagnostic assessment feels that an alternative syndrome is more likely than PD"
    }
]

const RED_FLAGS = [
    {
        id: 'pd_rf_1',
        label: 'Rapid progression of gait impairment requiring regular use of wheelchair within 5 years of onset'
    },
    {
        id: 'pd_rf_2',
        label: 'A complete absence of progression of motor symptoms or signs over 5 or more years unless stability is related to treatment'
    },
    {
        id: 'pd_rf_3',
        label: 'Early bulbar dysfunction: severe dysphonia or dysarthria (speech unintelligible most of the time) or severe dysphagia (requiring soft food, NG tube, or gastrostomy feeding) within first 5 years'
    },
    {
        id: 'pd_rf_4',
        label: 'Inspiratory respiratory dysfunction: either diurnal or nocturnal inspiratory stridor or frequent inspiratory sighs'
    },
    {
        id: 'pd_rf_5',
        label: 'Severe autonomic failure in the first 5 years of disease. This can include: a) Orthostatic hypotension — orthostatic decrease of blood pressure within 3 min of standing by at least 30 mm Hg systolic or 15 mm Hg diastolic, in the absence of dehydration, medication, or other diseases that could plausibly explain autonomic dysfunction, or b) Severe urinary retention or urinary incontinence in the first 5 years of disease (excluding long-standing or small amount stress incontinence in women), that is not simply functional incontinence. In men, urinary retention must not be attributable to prostate disease, and must be associated with erectile dysfunction'
    },
    {
        id: 'pd_rf_6',
        label: 'Recurrent (>1/year) falls because of impaired balance within 3 years of onset'
    },
    {
        id: 'pd_rf_7',
        label: 'Disproportionate anterocollis (dystonic) or contractures of hand or feet within the first 10 years'
    },
    {
        id: 'pd_rf_8',
        label: 'Absence of any of the common nonmotor features of disease despite 5 years disease duration. These include sleep dysfunction (sleep-maintenance insomnia, excessive daytime somnolence, symptoms of REM sleep behavior disorder), autonomic dysfunction (constipation, daytime urinary urgency, symptomatic orthostasis), hyposmia, or psychiatric dysfunction (depression, anxiety, or hallucinations)'
    },
    {
        id: 'pd_rf_9',
        label: 'Otherwise-unexplained pyramidal tract signs, defined as pyramidal weakness or clear pathologic hyperreflexia (excluding mild reflex asymmetry and isolated extensor plantar response)'
    },
    {
        id: 'pd_rf_10',
        label: 'Bilateral symmetric parkinsonism. The patient or caregiver reports bilateral symptom onset with no side predominance, and no side predominance is observed on objective examination'
    }
]

const ALL_CRITERIA_IDS = [
    ...SUPPORTIVE_CRITERIA.map(c => c.id),
    ...ABSOLUTE_EXCLUSION_CRITERIA.map(c => c.id),
    ...RED_FLAGS.map(c => c.id),
    'pd_has_parkinsonism'
]

function computeMDSDiagnosis(localData) {
    if (localData['pd_has_parkinsonism'] !== 'Yes') return null

    const hasAbsoluteExclusion = ABSOLUTE_EXCLUSION_CRITERIA.some(c => localData[c.id] === 'Yes')
    if (hasAbsoluteExclusion) return { text: 'Cannot diagnose PD (absolute exclusion criteria present)', variant: 'dark', established: 'No', probable: 'No' }

    const supportiveCount = SUPPORTIVE_CRITERIA.filter(c => localData[c.id] === 'Yes').length
    const redFlagCount = RED_FLAGS.filter(c => localData[c.id] === 'Yes').length

    if (redFlagCount > 2) {
        return { text: 'Cannot diagnose Probable PD (more than 2 red flags)', variant: 'dark', established: 'No', probable: 'No' }
    }
    if (supportiveCount >= 2 && redFlagCount === 0) {
        return { text: 'Clinically Established PD', variant: 'primary', established: 'Yes', probable: 'No' }
    }
    // Probable PD: red flags 0→any supportive, 1→≥1, 2→≥2  (i.e. supportive ≥ redFlags)
    if (supportiveCount >= redFlagCount) {
        return { text: 'Clinically Probable PD', variant: 'info', established: 'No', probable: 'Yes' }
    }
    return { text: 'Criteria not met for PD diagnosis', variant: 'secondary', established: 'No', probable: 'No' }
}

function ParkinsonsDisease(props) {
    const [localData, setLocalData] = useState({})

    useEffect(() => {
        if (props.data != null) {
            setLocalData(props.data)
        }
    }, [props.data])

    const handler = (event) => {
        props.handler("clinical_parkinson_disease", event)
    }

    // Fires handler for a single field
    const fireField = (name, value) => {
        handler({ target: { name, value } })
    }

    // Handler for the legacy dropdowns — no MDS auto-computation
    const handleLegacySelect = (e) => {
        setLocalData(prev => ({ ...prev, [e.target.name]: e.target.value }))
        handler(e)
    }

    // Handler for any MDS criteria field — auto-computes and propagates to existing fields
    const handleMDSField = (name, value) => {
        const newData = { ...localData, [name]: value }
        const result = computeMDSDiagnosis(newData)

        // Build state update: always include the changed field
        const stateUpdate = { ...newData }

        // If MDS criteria produced a result, apply it to the existing DB fields
        if (result) {
            stateUpdate['clinically_established'] = result.established
            stateUpdate['probable'] = result.probable
        }

        setLocalData(stateUpdate)
        fireField(name, value)

        // Propagate updated diagnosis fields to parent so they are saved
        if (result) {
            fireField('clinically_established', result.established)
            fireField('probable', result.probable)
        }
    }

    const handleMDSSelect = (e) => handleMDSField(e.target.name, e.target.value)
    const handleCheckbox = (e) => handleMDSField(e.target.name, e.target.checked ? 'Yes' : '')

    const diagnosis = computeMDSDiagnosis(localData)
    const supportiveCount = SUPPORTIVE_CRITERIA.filter(c => localData[c.id] === 'Yes').length
    const redFlagCount = RED_FLAGS.filter(c => localData[c.id] === 'Yes').length
    const hasAbsoluteExclusion = ABSOLUTE_EXCLUSION_CRITERIA.some(c => localData[c.id] === 'Yes')
    // True when the MDS criteria workflow has been started (at least parkinsonism answered)
    const hasMDSData = !!localData['pd_has_parkinsonism']

    return (
        <>
            <Title>{props.title}</Title>

            {/* ── Fixed bottom diagnosis bar ── */}
            <div style={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 1000,
                background: '#e9ecef',
                borderTop: '3px solid #495057',
                padding: '12px 28px',
                display: 'flex',
                alignItems: 'center',
                gap: '28px',
                boxShadow: '0 -4px 12px rgba(0,0,0,0.15)',
            }}>
                <strong style={{ whiteSpace: 'nowrap', fontSize: '1em' }}>Diagnosis:</strong>

                <Form.Group style={{ marginBottom: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Form.Label style={{ marginBottom: 0, whiteSpace: 'nowrap', fontSize: '0.95em', fontWeight: '500' }}>
                        Clinically established
                        {hasMDSData && <Badge variant="info" style={{ marginLeft: '6px', fontSize: '0.7em' }}>auto</Badge>}
                    </Form.Label>
                    <Form.Control as="select" onChange={handleLegacySelect} name="clinically_established"
                        value={localData['clinically_established'] || ''} disabled={props.edit || hasMDSData}
                        style={{ width: '100px', fontSize: '0.95em' }}>
                        <option value=""></option>
                        <option>Yes</option>
                        <option>No</option>
                    </Form.Control>
                </Form.Group>

                <Form.Group style={{ marginBottom: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Form.Label style={{ marginBottom: 0, whiteSpace: 'nowrap', fontSize: '0.95em', fontWeight: '500' }}>
                        Probable
                        {hasMDSData && <Badge variant="info" style={{ marginLeft: '6px', fontSize: '0.7em' }}>auto</Badge>}
                    </Form.Label>
                    <Form.Control as="select" onChange={handleLegacySelect} name="probable"
                        value={localData['probable'] || ''} disabled={props.edit || hasMDSData}
                        style={{ width: '100px', fontSize: '0.95em' }}>
                        <option value=""></option>
                        <option>Yes</option>
                        <option>No</option>
                    </Form.Control>
                </Form.Group>

                {diagnosis && (
                    <Badge variant={diagnosis.variant} style={{ fontSize: '0.95em', padding: '8px 12px' }}>
                        {diagnosis.text}
                    </Badge>
                )}
            </div>

            <div style={{ paddingBottom: '70px' }}>

            {/* ── MDS Table 1 ── */}
            <h5 style={{ marginTop: '16px', marginBottom: '8px' }}>
                MDS Clinical Diagnostic Criteria for Parkinson's Disease 
            </h5>
            <p style={{ fontSize: '0.88em', color: '#555', marginBottom: '16px' }}>
                Parkinsonism is defined as bradykinesia, in combination with at least 1 of rest tremor or rigidity.
            </p>

            <Form.Group>
                <Form.Label><strong>Step 1: Does the patient have parkinsonism (as defined by the MDS criteria)?</strong></Form.Label>
                <Form.Control as="select" onChange={handleMDSSelect} name="pd_has_parkinsonism"
                    value={localData['pd_has_parkinsonism'] || ''} disabled={props.edit}
                    style={{ maxWidth: '200px' }}>
                    <option value=""></option>
                    <option>Yes</option>
                    <option>No</option>
                </Form.Control>
                {localData['pd_has_parkinsonism'] === 'No' && (
                    <p style={{ color: '#c0392b', marginTop: '6px', fontSize: '0.88em' }}>
                        If No, neither probable PD nor clinically established PD can be diagnosed.
                    </p>
                )}
            </Form.Group>

            {/* ── Supportive Criteria ── */}
            <Card style={{ marginBottom: '16px', border: '1px solid #28a745' }}>
                <Card.Header style={{ background: '#d4edda', fontWeight: 'bold', padding: '8px 12px' }}>
                    Supportive Criteria
                    <span style={{ float: 'right', fontSize: '0.82em', fontWeight: 'normal' }}>Check box if criteria met</span>
                </Card.Header>
                <Card.Body style={{ padding: '0' }}>
                    <Table size="sm" style={{ marginBottom: 0 }}>
                        <thead>
                            <tr>
                                <th style={{ width: '36px' }}>#</th>
                                <th>Criterion</th>
                                <th style={{ width: '70px', textAlign: 'center' }}>Present</th>
                            </tr>
                        </thead>
                        <tbody>
                            {SUPPORTIVE_CRITERIA.map((c, i) => (
                                <tr key={c.id}>
                                    <td>{i + 1}</td>
                                    <td style={{ fontSize: '0.88em' }}>{c.label}</td>
                                    <td style={{ textAlign: 'center' }}>
                                        <Form.Check
                                            type="checkbox"
                                            id={c.id}
                                            label=""
                                            name={c.id}
                                            checked={localData[c.id] === 'Yes'}
                                            onChange={handleCheckbox}
                                            disabled={props.edit}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>

            {/* ── Absolute Exclusion Criteria ── */}
            <Card style={{ marginBottom: '16px', border: '1px solid #6f42c1' }}>
                <Card.Header style={{ background: '#ede7f6', fontWeight: 'bold', padding: '8px 12px' }}>
                    Absolute Exclusion Criteria
                    <span style={{ float: 'right', fontSize: '0.82em', fontWeight: 'normal' }}>The presence of any of these features rules out PD</span>
                </Card.Header>
                <Card.Body style={{ padding: '0' }}>
                    <Table size="sm" style={{ marginBottom: 0 }}>
                        <thead>
                            <tr>
                                <th style={{ width: '36px' }}>#</th>
                                <th>Criterion</th>
                                <th style={{ width: '70px', textAlign: 'center' }}>Present</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ABSOLUTE_EXCLUSION_CRITERIA.map((c, i) => (
                                <tr key={c.id}>
                                    <td>{i + 1}</td>
                                    <td style={{ fontSize: '0.88em' }}>{c.label}</td>
                                    <td style={{ textAlign: 'center' }}>
                                        <Form.Check
                                            type="checkbox"
                                            id={c.id}
                                            label=""
                                            name={c.id}
                                            checked={localData[c.id] === 'Yes'}
                                            onChange={handleCheckbox}
                                            disabled={props.edit}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>

            {/* ── Red Flags ── */}
            <Card style={{ marginBottom: '16px', border: '1px solid #dc3545' }}>
                <Card.Header style={{ background: '#f8d7da', fontWeight: 'bold', padding: '8px 12px' }}>
                    Red Flags
                </Card.Header>
                <Card.Body style={{ padding: '0' }}>
                    <Table size="sm" style={{ marginBottom: 0 }}>
                        <thead>
                            <tr>
                                <th style={{ width: '36px' }}>#</th>
                                <th>Criterion</th>
                                <th style={{ width: '70px', textAlign: 'center' }}>Present</th>
                            </tr>
                        </thead>
                        <tbody>
                            {RED_FLAGS.map((c, i) => (
                                <tr key={c.id}>
                                    <td>{i + 1}</td>
                                    <td style={{ fontSize: '0.88em' }}>{c.label}</td>
                                    <td style={{ textAlign: 'center' }}>
                                        <Form.Check
                                            type="checkbox"
                                            id={c.id}
                                            label=""
                                            name={c.id}
                                            checked={localData[c.id] === 'Yes'}
                                            onChange={handleCheckbox}
                                            disabled={props.edit}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>

            {/* ── Criteria Application ── */}
            <Card style={{ marginBottom: '20px' }}>
                <Card.Header style={{ fontWeight: 'bold', padding: '8px 12px' }}>Criteria Application</Card.Header>
                <Card.Body style={{ padding: '12px' }}>
                    <table style={{ width: '100%' }}>
                        <tbody>
                            <tr>
                                <td style={{ padding: '4px 0', width: '65%' }}>1. Does the patient have parkinsonism?</td>
                                <td>
                                    <Badge variant={localData['pd_has_parkinsonism'] === 'Yes' ? 'success' : localData['pd_has_parkinsonism'] === 'No' ? 'danger' : 'secondary'}>
                                        {localData['pd_has_parkinsonism'] || '—'}
                                    </Badge>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: '4px 0' }}>2. Are any absolute exclusion criteria present?</td>
                                <td>
                                    <Badge variant={hasAbsoluteExclusion ? 'danger' : 'success'}>
                                        {hasAbsoluteExclusion ? 'Yes' : 'No'}
                                    </Badge>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: '4px 0' }}>3. Number of red flags present</td>
                                <td>
                                    <Badge variant={redFlagCount === 0 ? 'success' : redFlagCount <= 2 ? 'warning' : 'danger'}>
                                        {redFlagCount}
                                    </Badge>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: '4px 0' }}>4. Number of supportive criteria present</td>
                                <td>
                                    <Badge variant={supportiveCount >= 2 ? 'success' : supportiveCount === 1 ? 'warning' : 'secondary'}>
                                        {supportiveCount}
                                    </Badge>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: '4px 0' }}>5. More than 2 red flags?</td>
                                <td>
                                    <Badge variant={redFlagCount > 2 ? 'danger' : 'success'}>
                                        {redFlagCount > 2 ? 'Yes → Probable PD cannot be diagnosed' : 'No'}
                                    </Badge>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: '4px 0' }}>6. At least 2 supportive criteria and no red flags?</td>
                                <td>
                                    <Badge variant={supportiveCount >= 2 && redFlagCount === 0 ? 'success' : 'secondary'}>
                                        {supportiveCount >= 2 && redFlagCount === 0 ? 'Yes → Clinically Established PD' : 'No'}
                                    </Badge>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: '4px 0' }}>7. Supportive criteria ≥ red flags (0→any, 1→≥1, 2→≥2)?</td>
                                <td>
                                    <Badge variant={redFlagCount <= 2 && supportiveCount >= redFlagCount ? 'success' : 'secondary'}>
                                        {redFlagCount <= 2 && supportiveCount >= redFlagCount ? 'Yes → Clinically Probable PD' : 'No'}
                                    </Badge>
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    {diagnosis && (
                        <div style={{
                            marginTop: '12px', padding: '10px 14px', borderRadius: '4px',
                            background: diagnosis.variant === 'primary' ? '#cce5ff' : diagnosis.variant === 'info' ? '#d1ecf1' : diagnosis.variant === 'dark' ? '#d6d8d9' : '#e2e3e5'
                        }}>
                            <strong>MDS Criteria Result: </strong>
                            <Badge variant={diagnosis.variant} style={{ fontSize: '0.92em', marginLeft: '6px' }}>
                                {diagnosis.text}
                            </Badge>
                        </div>
                    )}
                </Card.Body>
            </Card>

            </div>{/* end content wrapper */}
        </>
    )
}

ParkinsonsDisease.defaultProps = {
    data: null
}

export default ParkinsonsDisease
